import React, { Component } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { string, func, bool, shape, number } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { lazyLoadWithDimensions } from '../../util/contextHelpers';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import config from '../../config';
import { NamedLink, ResponsiveImage, DeliveryBadge, ImageCarousel, ImageSlider } from '../../components';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { withViewport } from '../../util/contextHelpers';

import css from './ListingCard.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const MAX_MOBILE_SCREEN_WIDTH = 767;

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const calculateDistance = (lat1, lon1, lat2, lon2, unit) => {
  if (lat1 === lat2 && lon1 === lon2) {
    return '0';
  } else {
    const radlat1 = (Math.PI * lat1) / 180;
    const radlat2 = (Math.PI * lat2) / 180;
    const theta = lon1 - lon2;
    const radtheta = (Math.PI * theta) / 180;
    let dist =
      Math.sin(radlat1) * Math.sin(radlat2) +
      Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515;
    if (unit === 'K') {
      dist = dist * 1.609344;
    }
    if (unit === 'N') {
      dist = dist * 0.8684;
    }
    return dist.toFixed(2);
  }
};

class ListingImage extends Component {
  render() {
    return <ResponsiveImage {...this.props} />;
  }
}
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

export const ListingCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    shouldShowDistance,
    origin,
    isOnSearchPage,
    history,
    viewport,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData, geolocation } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const { deliveryType, location, pricingType } = publicData || {};
  const { suburb } = location || {};
  const { formattedPrice, priceTitle } = priceData(price, intl);

  const priceDuration = config.custom.priceDurationConfig.find(ele => ele.key === pricingType);

  const unitTranslationKey = priceDuration ? priceDuration.labelListingId : 'ListingCard.perDay';

  const area =
    shouldShowDistance && origin && geolocation
      ? calculateDistance(geolocation.lat, geolocation.lng, origin.lat, origin.lng, 'K')
      : null;

  const areaDisplay = area
    ? intl.formatMessage({ id: 'ListingCard.distance' }, { distance: area })
    : null;

  const aspectWrapperClasses = classNames(css.aspectWrapper, {
    [css.carouselWrapper]: isOnSearchPage,
  });

  const routes = routeConfiguration();

  const { width } = viewport;
  const hasViewport = width > 0;
  const isOnMobileScreen = hasViewport && width <= MAX_MOBILE_SCREEN_WIDTH;

  return (
    <div className={classes}>
      <div
        className={classNames(css.threeToTwoWrapper, css.cardCursor)}
        onMouseEnter={() => setActiveListing(currentListing.id)}
        onMouseLeave={() => setActiveListing(null)}
        onClick={() => {
          history.push(
            createResourceLocatorString(
              "ListingPage",
              routes,
              { id: currentListing.id.uuid, slug: createSlug(currentListing.attributes.title) },
              {}
            )
          );
        }}
      >
        <div className={aspectWrapperClasses}>
          {isOnSearchPage ? (
            isOnMobileScreen
              ? <ImageSlider className={css.carouselForImage} images={currentListing.images} useDots />
              : <ImageCarousel
                className={css.carouselForImage}
                images={currentListing.images}
                buttonClassName={css.carouselButton}
              />
          ) : (
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={['default']}
              sizes={renderSizes}
            />
          )}
        </div>
        <DeliveryBadge className={css.deliveryBadge} deliveryType={deliveryType} />
      </div>
      <NamedLink name="ListingPage" params={{ id, slug }}>
        <div className={css.info}>
          <div className={css.price}>
            <div className={css.priceValue} title={priceTitle}>
              {formattedPrice}
            </div>
            <div className={css.perUnit}>
              <FormattedMessage id={unitTranslationKey} />
            </div>
          </div>
          <div className={css.mainInfo}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.hostedBy" values={{ authorName }} />
            </div>
            <div className={css.location}>{suburb}</div>
          </div>
        </div>
        <div className={css.distance}>{areaDisplay}</div>
      </NamedLink>
    </div>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: () => null,
  isOnSearchPage: false,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  isOnSearchPage: bool.isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default compose(injectIntl, withRouter, withViewport)(ListingCardComponent);
